/**
 * 基本設計の動く図（API の `GET /{bdKey}/graph`）を、画面に置く位置・色・強調に直す。
 *
 * 列（layer）は API が決める。全体図は「業務 → 画面 → 窓口 → 持ち主 → データ → 判断」、ER 図と
 * 画面遷移図は線をたどった深さ。ここは列を横（LR）か縦（TB）に並べ、列の中を API が返した順に
 * 積むだけにする（同じ行からはいつも同じ配置になり、読み手が場所で覚えられる）。
 *
 * 隠した列は詰めて置く。空いた列を残すと、隠すほど図が横に間延びする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の動く図のノードを列ごとに並べ、変更の種類と影響の向きで色を付け、選んだノードとつながるノードを強調する
 * @graph-connects none
 */

import type { BdGraph, BdGraphEdge, BdGraphNode } from '../../../lib/api/basic-design.js';
import type { BdGraphDirection } from '../../../lib/bd-view-settings.js';

/** 札の幅（px）。札の `w-52` と揃える。 @graph-connects none */
export const BD_GRAPH_NODE_WIDTH = 208;

/**
 * 列と列の間隔（px）。札の幅に、列の脇を回す線（`side`）の名前が隣の列の札にかからずに
 * 収まる長さを足す。
 * @graph-connects none
 */
export const BD_GRAPH_COLUMN_GAP = 480;

/** 縦に並べるときの、同じ列の札どうしの横の間隔（px）。 @graph-connects none */
export const BD_GRAPH_SIBLING_GAP_TB = 240;

/** 名前の札の幅の下限（px）。これより狭いと何の線か読めない。 @graph-connects none */
const LOOP_LABEL_MIN_WIDTH = 80;

/** 名前の札の幅の上限（px）。 @graph-connects none */
const LOOP_LABEL_MAX_WIDTH = 176;

/** 列の中の札の間隔（px）。2 行の札が重ならない高さ。 @graph-connects none */
export const BD_GRAPH_ROW_GAP = 96;

/** 縦に並べるときの列と列の間隔（px）。線の名前が札にかからない長さを残す。 @graph-connects none */
export const BD_GRAPH_LAYER_GAP_TB = 180;

/** 札の色の種類。 @graph-connects none */
export type BdGraphTone =
  | 'new'
  | 'modified'
  | 'removed'
  | 'unchanged'
  | 'reused'
  | 'decision'
  | 'domain'
  | 'degradation'
  | 'dependency'
  | 'improvement'
  | 'neutral';

/** 変更の種類の値で色を決めるもの。 @graph-connects none */
const IMPACT_TONES: Readonly<Record<string, BdGraphTone>> = {
  new: 'new',
  modified: 'modified',
  removed: 'removed',
  unchanged: 'unchanged',
  reused: 'reused',
};

/** 影響の向きで色を決めるもの。新規・影響なしは変更の種類と同じ色に寄せる。 @graph-connects none */
const DIRECTION_TONES: Readonly<Record<string, BdGraphTone>> = {
  degradation: 'degradation',
  dependency: 'dependency',
  improvement: 'improvement',
  new: 'new',
  safe: 'unchanged',
};

/**
 * 札の色。Mermaid の classDef と同じ決め方（判断・ドメインは種類で、ほかは変更の種類、業務
 * フローのノードは影響の向き）。
 * @graph-connects none
 */
export function bdGraphToneOf(
  node: Pick<BdGraphNode, 'kind' | 'impact' | 'direction'>,
): BdGraphTone {
  if (node.kind === 'decision') return 'decision';
  if (node.kind === 'domain') return 'domain';
  const byImpact = node.impact === undefined ? undefined : IMPACT_TONES[node.impact];
  if (byImpact !== undefined) return byImpact;
  const byDirection = node.direction === undefined ? undefined : DIRECTION_TONES[node.direction];
  return byDirection ?? 'neutral';
}

/** 札の色のクラス（背景・枠・文字）。 @graph-connects none */
export const BD_GRAPH_TONE_CLASS: Readonly<Record<BdGraphTone, string>> = {
  new: 'border-[var(--color-success)] bg-[var(--color-success-muted)] text-[var(--color-success-text)]',
  modified:
    'border-[var(--color-warning)] bg-[var(--color-warning-muted)] text-[var(--color-warning-text)]',
  removed:
    'border-dashed border-[var(--color-error)] bg-[var(--color-error-muted)] text-[var(--color-error-text)]',
  unchanged:
    'border-[var(--color-border-hover)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]',
  reused:
    'border-[var(--color-info)] bg-[var(--color-info-muted)] text-[var(--color-text-primary)]',
  decision:
    'border-[var(--color-accent-primary)] bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]',
  domain:
    'border-[var(--color-info)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]',
  degradation:
    'border-[var(--color-error)] bg-[var(--color-error-muted)] text-[var(--color-error-text)]',
  dependency:
    'border-[var(--color-warning)] bg-[var(--color-warning-muted)] text-[var(--color-warning-text)]',
  improvement:
    'border-[var(--color-success)] bg-[var(--color-success-muted)] text-[var(--color-success-text)]',
  neutral:
    'border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]',
};

/** 図に出す列の番号（昇順）。 @graph-connects none */
export function bdGraphLayers(graph: BdGraph): number[] {
  return [...new Set(graph.nodes.map((n) => n.layer))].sort((a, b) => a - b);
}

/** ノードにつながるノード（自分を含む）。 @graph-connects none */
export function bdGraphNeighbours(edges: readonly BdGraphEdge[], id: string): Set<string> {
  const ids = new Set([id]);
  for (const edge of edges) {
    if (edge.source === id) ids.add(edge.target);
    if (edge.target === id) ids.add(edge.source);
  }
  return ids;
}

/** 置き方の指定。 @graph-connects none */
export interface BdGraphLayoutOptions {
  /** 隠す列 */
  hiddenLayers: readonly number[];
  direction: BdGraphDirection;
  /** この判断（ノード ID）とつながるノードだけを出す。null なら全部 */
  decision: string | null;
  /** 選んだノード。つながるノードを強調し、ほかを薄くする */
  selected: string | null;
}

/** 置いた札 1 つ。 @graph-connects none */
export interface BdPlacedNode {
  node: BdGraphNode;
  x: number;
  y: number;
  tone: BdGraphTone;
  /** 選んだノードとつながらないので薄くする */
  dimmed: boolean;
  selected: boolean;
}

/** 置いた線 1 本。 @graph-connects none */
export interface BdPlacedEdge {
  edge: BdGraphEdge;
  dimmed: boolean;
  /** 選んだノードにつながる線 */
  highlighted: boolean;
  /** 線の引き方。 */
  route: BdEdgeRoute;
  /** `around` / `side` の線の何本目か（0 始まり）。同じ 2 枚の間を回る線どうしの名前が重ならないよう、外へずらす */
  lane: number;
}

/**
 * 線の引き方。
 * - `straight`: 前の列から後ろの列へ、札の出口から入口へまっすぐ引く
 * - `reverse`: 後ろの列から前の列へ。札を突き抜けないよう、出入り口を左右（縦の図では上下）入れ替える
 * - `around`: 違う列の同じ 2 枚の間の 2 本目以降。まっすぐ引くと線と名前が重なるため、札の下
 *   （縦の図では右）を回す
 * - `side`: 同じ列どうしの線。札が列の中で縦（縦の図では横）に並ぶので、下を回すと隣の札を
 *   突き抜ける。列の脇（横の図では左、縦の図では上）を回す
 * @graph-connects none
 */
export type BdEdgeRoute = 'straight' | 'reverse' | 'around' | 'side';

/** 回す線が札から出ていく辺。 @graph-connects none */
export type BdLoopSide = 'bottom' | 'right' | 'left' | 'top';

/** 回す線の、札から 1 本目までの距離（px）。 @graph-connects none */
export const BD_LOOP_OFFSET = 28;

/** 回す線の 2 本目以降の間隔（px）。 @graph-connects none */
export const BD_LOOP_LANE_GAP = 30;

/** 縦に回す線の名前を、何本目かごとに縦へずらす幅（px）。名前の札 1 枚の高さより広くとる。 @graph-connects none */
export const BD_LOOP_LABEL_STAGGER = 22;

/**
 * 回す線の名前の置き場所。`align` は名前の札を置き場所のどちら側に寄せるか
 * （`start` は右へ、`end` は左へはみ出させ、線そのものには重ねない）。
 * @graph-connects none
 */
export interface BdLoopLabelPlacement {
  x: number;
  y: number;
  align: 'center' | 'start' | 'end';
  /** 名前の札の幅の上限（px）。脇へ寄せるときは隣の列（札）までの空きに収める */
  maxWidth: number;
}

/**
 * 回す線が札から離れる距離。何本目かごとに外へずらす。
 * @param lane - 同じ 2 枚の間を回る線の何本目か
 * @returns 札からの距離（px）
 * @graph-connects none
 */
export function bdLoopOffset(lane: number): number {
  return BD_LOOP_OFFSET + lane * BD_LOOP_LANE_GAP;
}

/**
 * 回す線の名前を、線が札の外を通る区間の上に置く。React Flow の既定は両端の真ん中に置くため、
 * 同じ 2 枚の間を回る線どうしで名前が同じ場所に重なる。
 * @graph-connects bdLoopOffset [calls] 札から離れる距離
 */
export function bdLoopLabelPlacement(input: {
  side: BdLoopSide;
  lane: number;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}): BdLoopLabelPlacement {
  const { side, lane, sourceX, sourceY, targetX, targetY } = input;
  const offset = bdLoopOffset(lane);
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2 + lane * BD_LOOP_LABEL_STAGGER;
  const maxWidth = LOOP_LABEL_MAX_WIDTH;
  // 脇へ寄せる名前は、線から隣の列（縦の図では隣の札）の端までの空きに収める
  const besideWidth = (gap: number) =>
    Math.min(
      LOOP_LABEL_MAX_WIDTH,
      Math.max(LOOP_LABEL_MIN_WIDTH, gap - BD_GRAPH_NODE_WIDTH - offset - 8),
    );
  switch (side) {
    case 'bottom':
      return { x: midX, y: Math.max(sourceY, targetY) + offset, align: 'center', maxWidth };
    case 'top':
      return { x: midX, y: Math.min(sourceY, targetY) - offset, align: 'center', maxWidth };
    case 'right':
      return {
        x: Math.max(sourceX, targetX) + offset,
        y: midY,
        align: 'start',
        maxWidth: besideWidth(BD_GRAPH_SIBLING_GAP_TB),
      };
    case 'left':
      return {
        x: Math.min(sourceX, targetX) - offset,
        y: midY,
        align: 'end',
        maxWidth: besideWidth(BD_GRAPH_COLUMN_GAP),
      };
    default:
      return { x: midX, y: midY, align: 'center', maxWidth };
  }
}

/** 置いた図。 @graph-connects none */
export interface BdGraphLayout {
  nodes: BdPlacedNode[];
  edges: BdPlacedEdge[];
}

/**
 * 図を置く。隠した列・判断の絞り込みで外れたノードは出さず、両端が出ている線だけを残す。
 * @graph-connects bdGraphNeighbours [calls] つながるノード
 * @graph-connects bdGraphToneOf [calls] 札の色
 */
export function layoutBdGraph(graph: BdGraph, options: BdGraphLayoutOptions): BdGraphLayout {
  const hidden = new Set(options.hiddenLayers);
  const decisionScope =
    options.decision === null ? null : bdGraphNeighbours(graph.edges, options.decision);
  const visible = graph.nodes.filter(
    (n) => !hidden.has(n.layer) && (decisionScope === null || decisionScope.has(n.id)),
  );
  const visibleIds = new Set(visible.map((n) => n.id));
  const selected =
    options.selected !== null && visibleIds.has(options.selected) ? options.selected : null;
  const focus = selected === null ? null : bdGraphNeighbours(graph.edges, selected);

  const columns = [...new Set(visible.map((n) => n.layer))].sort((a, b) => a - b);
  const placedInColumn = new Map<number, number>();
  const nodes = visible.map((node) => {
    const column = columns.indexOf(node.layer);
    const row = placedInColumn.get(node.layer) ?? 0;
    placedInColumn.set(node.layer, row + 1);
    // 縦に並べるときは、列が行に、列の中の並びが横になる
    const lr = options.direction === 'LR';
    return {
      node,
      x: lr ? column * BD_GRAPH_COLUMN_GAP : row * BD_GRAPH_SIBLING_GAP_TB,
      y: lr ? row * BD_GRAPH_ROW_GAP : column * BD_GRAPH_LAYER_GAP_TB,
      tone: bdGraphToneOf(node),
      dimmed: focus !== null && !focus.has(node.id),
      selected: node.id === selected,
    };
  });
  const columnOf = new Map(visible.map((n) => [n.id, columns.indexOf(n.layer)]));
  // 2 枚の組（向きは問わない）ごとに、まっすぐ引いた線があるかと、回した線の本数を数える
  const straightPairs = new Set<string>();
  const aroundCount = new Map<string, number>();
  const edges = graph.edges
    .filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target))
    .map((edge) => {
      const touches = selected !== null && (edge.source === selected || edge.target === selected);
      const pair = [edge.source, edge.target].sort().join('|');
      const from = columnOf.get(edge.source) ?? 0;
      const to = columnOf.get(edge.target) ?? 0;
      let route: BdEdgeRoute = from === to ? 'side' : 'around';
      let lane = 0;
      if (from !== to && !straightPairs.has(pair)) {
        straightPairs.add(pair);
        route = to > from ? 'straight' : 'reverse';
      } else {
        lane = aroundCount.get(pair) ?? 0;
        aroundCount.set(pair, lane + 1);
      }
      return { edge, highlighted: touches, dimmed: selected !== null && !touches, route, lane };
    });
  return { nodes, edges };
}

/** 判断のノード（絞り込みの選択肢）。 @graph-connects none */
export function bdGraphDecisions(graph: BdGraph): BdGraphNode[] {
  return graph.nodes.filter((n) => n.kind === 'decision');
}
